// Matematica de la cronologia. Igual que flowGeometry: funciones puras, sin
// React, para poder ejecutarlas con node y probarlas.

export const PX_DIA = { dia: 34, semana: 11, mes: 3.6 };
export const ROW_H  = { dia: 34, semana: 30, mes: 26 };
export const ESCALAS = [
  { key: "dia",    label: "Dia",    icon: "calendar3" },
  { key: "semana", label: "Semana", icon: "calendar-week" },
  { key: "mes",    label: "Mes",    icon: "calendar-x" },
];

// Fecha ISO -> milisegundos UTC de su medianoche.
//
// En UTC a proposito, y esto NO es opcional: con horas locales, el dia del
// cambio de hora tiene 23 h, y una division por 86400000 devuelve 0,958 dias.
// Redondeando eso a lo largo de un año, el Gantt sale corrido medio dia.
const aUTC = s => {
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return NaN;
  return Date.UTC(y, m - 1, d);
};

export const diasEntre = (a, b) => {
  const pa = aUTC(a), pb = aUTC(b);
  if (isNaN(pa) || isNaN(pb)) return 0;
  return Math.round((pb - pa) / 86400000);
};

// Lo que tiene que medir el proyecto ENTERO para que se pueda leer. Por debajo
// de esto las barras son astillas de pocos pixeles, y una barra que no se puede
// comparar con la de al lado no es un Gantt: es un adorno.
export const ANCHO_MIN_UTIL = 260;

// La escala mas FINA en la que el proyecto entero cabe en el ancho disponible.
//
// De fina a gruesa, y se queda con la primera que quepa: a igualdad de
// legibilidad se prefiere el detalle, porque el dia dice cosas que el mes no
// —que dos pasos se solapan, que uno empieza el lunes— y ampliar es gratis
// mientras no haya que hacer scroll para ver el proyecto.
//
// Si no cabe en ninguna, devuelve la mas gruesa: un proyecto de tres años no va
// a caber en pantalla y para eso esta el scroll.
export function escalaQueCabe(iso0, iso1, ancho = 900) {
  const dias = diasEntre(iso0, iso1) + 1;
  for (const e of ESCALAS) {
    if (dias * PX_DIA[e.key] <= ancho) return e.key;
  }
  return ESCALAS[ESCALAS.length - 1].key;
}

// ¿Llega el proyecto entero al ancho minimo legible con esta escala?
//
// Se llama asi y no "escalaSirve" porque no dice si la escala es la correcta:
// un proyecto de seis dias no alcanza el minimo NI EN LA MAS FINA, y no por eso
// hay nada que corregir. Solo sirve para lo que sirve: detectar que la escala
// recordada aplasta este proyecto, y entonces preguntar si hay otra mejor.
export const alcanzaElMinimo = (iso0, iso1, escala, minimo = ANCHO_MIN_UTIL) =>
  (diasEntre(iso0, iso1) + 1) * (PX_DIA[escala] || 0) >= minimo;


export const sumarDias = (iso, n) => {
  const t = aUTC(iso);
  if (isNaN(t)) return iso;
  const d = new Date(t + n * 86400000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
};

// "Hoy" en Madrid. Con toISOString() se va un dia en verano y en Canarias; este
// es el mismo truco que ya usa ResumenEquipo.
export const hoyISO = () =>
  new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Madrid" }).format(new Date());

export const xDe    = (iso, iso0, px) => diasEntre(iso0, iso) * px;
export const isoDeX = (x, iso0, px)   => sumarDias(iso0, Math.round(x / px));

// Dia de la semana en UTC: 0 = domingo. No se usa getDay() sobre una fecha local
// por el mismo motivo que lo demas.
export const dowUTC = iso => {
  const t = aUTC(iso);
  return isNaN(t) ? 0 : new Date(t).getUTCDay();
};
export const esFindeISO = iso => { const d = dowUTC(iso); return d === 0 || d === 6; };

// Ventana que abarca todo lo que hay que pintar, con holgura a los lados.
export function rangoDe(proyecto, tareas, hoy = hoyISO()) {
  const fechas = [];
  if (proyecto?.start_date) fechas.push(proyecto.start_date);
  if (proyecto?.end_date)   fechas.push(proyecto.end_date);
  for (const t of tareas || []) {
    if (t.start_date) fechas.push(t.start_date);
    if (t.due_date)   fechas.push(t.due_date);
  }
  if (!fechas.length) return { iso0: sumarDias(hoy, -7), iso1: sumarDias(hoy, 21) };
  const ord = fechas.filter(Boolean).map(f => String(f).slice(0, 10)).sort();
  let iso0 = ord[0], iso1 = ord[ord.length - 1];
  // Hoy siempre entra en la ventana: una cronologia donde no se ve el presente
  // no sirve para saber si vamos tarde.
  if (hoy < iso0) iso0 = hoy;
  if (hoy > iso1) iso1 = hoy;
  return { iso0: sumarDias(iso0, -3), iso1: sumarDias(iso1, 7) };
}

// Bandas de dias no laborables, FUSIONANDO los tramos contiguos: un sabado, un
// domingo y un lunes festivo son UN rectangulo, no tres. Con 18 meses a escala
// de dia eso son ~110 rects en vez de ~380.
export function bandasNoLaborables(iso0, iso1, festivos = {}, px) {
  const total = diasEntre(iso0, iso1);
  const out = [];
  let ini = null;
  for (let i = 0; i <= total; i++) {
    const iso = sumarDias(iso0, i);
    const no = esFindeISO(iso) || !!festivos[iso];
    if (no && ini === null) ini = i;
    if (!no && ini !== null) { out.push({ x: ini * px, w: (i - ini) * px }); ini = null; }
  }
  if (ini !== null) out.push({ x: ini * px, w: (total + 1 - ini) * px });
  return out;
}

// Marcas de la regla. Cada escala rotula lo que cabe: a 3,6 px por dia no se
// puede escribir un dia, y a 34 no hace falta rotular el mes en cada columna.
export function marcasRegla(iso0, iso1, escala, px) {
  const total = diasEntre(iso0, iso1);
  const dias = [], grupos = [];
  const MESES = ["ene", "feb", "mar", "abr", "may", "jun",
                 "jul", "ago", "sep", "oct", "nov", "dic"];

  if (escala === "dia") {
    for (let i = 0; i <= total; i++) {
      const iso = sumarDias(iso0, i);
      dias.push({ iso, x: i * px, w: px, texto: iso.slice(8, 10), finde: esFindeISO(iso) });
    }
  } else if (escala === "semana") {
    for (let i = 0; i <= total; i++) {
      const iso = sumarDias(iso0, i);
      if (dowUTC(iso) !== 1) continue;          // lunes
      dias.push({ iso, x: i * px, w: px * 7, texto: iso.slice(8, 10) + " " + MESES[Number(iso.slice(5, 7)) - 1] });
    }
  } else {
    for (let i = 0; i <= total; i++) {
      const iso = sumarDias(iso0, i);
      if (iso.slice(8, 10) !== "01") continue;  // dia 1
      dias.push({ iso, x: i * px, w: px * 30, texto: MESES[Number(iso.slice(5, 7)) - 1] });
    }
  }

  // Piso superior: el mes (o el año, a escala de mes)
  let ini = 0, clave = null;
  const claveDe = iso => escala === "mes" ? iso.slice(0, 4) : iso.slice(0, 7);
  for (let i = 0; i <= total + 1; i++) {
    const iso = i <= total ? sumarDias(iso0, i) : null;
    const k = iso ? claveDe(iso) : null;
    if (clave === null) { clave = k; ini = i; continue; }
    if (k !== clave) {
      const desde = sumarDias(iso0, ini);
      grupos.push({
        clave, x: ini * px, w: (i - ini) * px,
        texto: escala === "mes"
          ? clave
          : `${MESES[Number(desde.slice(5, 7)) - 1]} ${desde.slice(0, 4)}`,
      });
      clave = k; ini = i;
    }
  }
  return { dias, grupos, ancho: (total + 1) * px };
}

// Geometria de la barra de un paso.
//
// UNA barra, no dos superpuestas, y el motivo es de fondo: el tiempo real del
// cuaderno NO es un intervalo de fechas, son horas por dia. Una segunda barra
// sobre el mismo eje afirmaria "el trabajo ocupo de X a Y", y el cuaderno no
// dice eso: dice "5 h el martes y 2 h el jueves". Pintarlo como barra solida
// inventa una continuidad que no existe.
//
// Asi que: la barra es la ventana PREVISTA, y el relleno es el avance.
export function barraDe(t, iso0, px, minutos, hoy = hoyISO()) {
  const inicio = t.start_date || t.due_date;
  const fin    = t.due_date   || t.start_date;
  if (!inicio || !fin) return null;

  const x = xDe(inicio, iso0, px);
  // Un dia dura una celda, no cero: sin el +1 una tarea de un solo dia
  // desaparece.
  const w = Math.max(px * 0.6, (diasEntre(inicio, fin) + 1) * px);

  const est  = Number(t.estimacion_min) || 0;
  const real = Number(minutos) || 0;

  // De donde sale el avance, por orden de fiabilidad. Se devuelve `fuente` para
  // que la interfaz pueda decir que esta mirando en vez de fingir precision.
  let avance = 0, fuente = "estado";
  if (est) {
    avance = Math.min(1, real / est);
    fuente = "horas";
  } else if (t.status === "Completada") {
    avance = 1;
  } else if (t.status === "En progreso") {
    avance = 0.5;
  }

  const exceso = est && real > est ? (real - est) / est : 0;
  const vencida = fin < hoy && t.status !== "Completada" && t.status !== "Cancelada";
  // Cola de retraso: desde el fin previsto hasta hoy.
  const colaW = vencida ? Math.max(px * 0.5, diasEntre(fin, hoy) * px) : 0;

  return { x, w, avance, fuente, exceso, vencida, colaW, est, real, inicio, fin };
}

// Conector ortogonal entre dos barras. Con rodilla y no Bezier: en un Gantt las
// curvas se confunden con las propias barras.
export function conectorDe(a, b, filaA, filaB, alto) {
  if (!a || !b) return null;
  const x1 = a.x + a.w, y1 = filaA * alto + alto / 2;
  const x2 = b.x,       y2 = filaB * alto + alto / 2;
  const mx = Math.max(x1 + 8, x2 - 8);
  return { d: `M${x1},${y1} H${mx} V${y2} H${x2}`, x1, y1, x2, y2 };
}

// Dias laborables entre dos fechas, ambas incluidas. Hermano de diasLaborables
// de utils.js, que solo sabe de meses completos. Devuelve negativo si `b` va
// antes que `a`, que es lo que permite usarlo para el desvio de plazo.
export function diasLaborablesEntre(a, b, festivos = {}) {
  if (!a || !b) return 0;
  const signo = b < a ? -1 : 1;
  const [ini, fin] = signo > 0 ? [a, b] : [b, a];
  const total = diasEntre(ini, fin);
  let n = 0;
  for (let i = 0; i <= total; i++) {
    const iso = sumarDias(ini, i);
    if (!esFindeISO(iso) && !festivos[iso]) n++;
  }
  return signo * n;
}
